import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useRecaptchaEnterprise } from "@/hooks/useRecaptchaEnterprise";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Valid email is required"),
  company: z.string().min(1, "Company is required"),
  job_title: z.string().min(1, "Job title is required"),
  works_it: z.boolean(),
  works_ot: z.boolean(),
  company_size: z.string().optional(),
  country: z.string().optional(),
  environment_type: z.array(z.string()).optional(),
  ot_assets_range: z.string().optional(),
  nis2_status: z.string().optional(),
  beta_start_timeframe: z.string().optional(),
  main_challenge: z.string().optional(),
  other_comments: z.string().optional(),
}).refine((data) => data.works_it || data.works_ot, {
  message: "Please select at least IT or OT",
  path: ["works_it"],
});

type FormValues = z.infer<typeof formSchema>;

interface BetaSignupFormProps {
  onSuccess: () => void;
}

const environmentTypes = [
  "Industrial production",
  "Energy / utility",
  "Building automation",
  "Other",
];

export function BetaSignupForm({ onSuccess }: BetaSignupFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [optionalOpen, setOptionalOpen] = useState(false);
  const { executeRecaptcha } = useRecaptchaEnterprise();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      job_title: "",
      works_it: false,
      works_ot: false,
      company_size: "",
      country: "",
      environment_type: [],
      ot_assets_range: "",
      nis2_status: "",
      beta_start_timeframe: "",
      main_challenge: "",
      other_comments: "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    if (!executeRecaptcha) {
      setError("reCAPTCHA not loaded");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Execute reCAPTCHA Enterprise
      const token = await executeRecaptcha("beta_signup");
      
      if (!token) {
        throw new Error("Failed to get reCAPTCHA token");
      }

      // Call edge function
      const { error: functionError } = await supabase.functions.invoke("beta-signup", {
        body: {
          ...values,
          recaptcha_token: token,
        },
      });

      if (functionError) {
        throw functionError;
      }

      onSuccess();
    } catch (err: any) {
      console.error("Beta signup error:", err);
      setError(err.message || "Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {error && (
          <div className="rounded-md bg-danger/10 border border-danger/20 p-3 text-sm text-danger">
            {error}
          </div>
        )}

        {/* Required Fields */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Contact Information</h3>
          
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Your name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Work Email *</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="you@company.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company *</FormLabel>
                <FormControl>
                  <Input placeholder="Your company" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="job_title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Job Title *</FormLabel>
                <FormControl>
                  <Input placeholder="Your role" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-2">
            <Label>Do you work with IT or OT? *</Label>
            <div className="flex gap-4">
              <FormField
                control={form.control}
                name="works_it"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 cursor-pointer">IT</FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="works_ot"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 cursor-pointer">OT</FormLabel>
                  </FormItem>
                )}
              />
            </div>
            {form.formState.errors.works_it && (
              <p className="text-sm text-danger">{form.formState.errors.works_it.message}</p>
            )}
          </div>
        </div>

        {/* Optional Fields - Collapsible */}
        <Collapsible open={optionalOpen} onOpenChange={setOptionalOpen}>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="flex w-full items-center justify-between p-0 hover:bg-transparent"
            >
              <span className="text-sm font-semibold">Additional Information (Optional)</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${optionalOpen ? "rotate-180" : ""}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-4">
            <FormField
              control={form.control}
              name="company_size"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Size</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="1-10">1–10</SelectItem>
                      <SelectItem value="11-50">11–50</SelectItem>
                      <SelectItem value="51-200">51–200</SelectItem>
                      <SelectItem value="201-1000">201–1000</SelectItem>
                      <SelectItem value="1000+">1000+ employees</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country / Time Zone</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Sweden, CET" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="environment_type"
              render={() => (
                <FormItem>
                  <FormLabel>Type of Environment</FormLabel>
                  <div className="space-y-2">
                    {environmentTypes.map((type) => (
                      <FormField
                        key={type}
                        control={form.control}
                        name="environment_type"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(type)}
                                onCheckedChange={(checked) => {
                                  const current = field.value || [];
                                  const updated = checked
                                    ? [...current, type]
                                    : current.filter((v) => v !== type);
                                  field.onChange(updated);
                                }}
                              />
                            </FormControl>
                            <FormLabel className="!mt-0 cursor-pointer">{type}</FormLabel>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ot_assets_range"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Approximate Number of OT Assets</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="1-50">1–50</SelectItem>
                      <SelectItem value="51-200">51–200</SelectItem>
                      <SelectItem value="201-1000">201–1000</SelectItem>
                      <SelectItem value="1000+">1000+</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nis2_status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Are you affected by NIS2?</FormLabel>
                  <FormControl>
                    <RadioGroup onValueChange={field.onChange} defaultValue={field.value}>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="Yes" id="nis2-yes" />
                        <Label htmlFor="nis2-yes" className="cursor-pointer">Yes</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="No" id="nis2-no" />
                        <Label htmlFor="nis2-no" className="cursor-pointer">No</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="Not sure" id="nis2-unsure" />
                        <Label htmlFor="nis2-unsure" className="cursor-pointer">Not sure</Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="beta_start_timeframe"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Preferred Start Timeframe</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select timeframe" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Within 1 month">Within 1 month</SelectItem>
                      <SelectItem value="Within 3 months">Within 3 months</SelectItem>
                      <SelectItem value="Within 6 months">Within 6 months</SelectItem>
                      <SelectItem value="Later / not sure">Later / not sure</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="main_challenge"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Main Challenge You Want ComplyManager to Help With</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe your main challenge..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="other_comments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Other Comments</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Any other information you'd like to share..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CollapsibleContent>
        </Collapsible>

        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? "Submitting..." : "Join Beta Program"}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          This site is protected by reCAPTCHA and the Google{" "}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">
            Privacy Policy
          </a>{" "}
          and{" "}
          <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">
            Terms of Service
          </a>{" "}
          apply.
        </p>
      </form>
    </Form>
  );
}
